import React, { useContext, useState, useEffect } from "react";
import AuthContext from "../../lib/auth-context";
import ThemeContext from "../../lib/theme-context";
import { accountService } from "../../services/api/accountService";
import throwMessage from "../../utils/throwMessage";
import CustomDatePicker from "../../components/dateTimePickers/datePicker";
import dayjs from "dayjs";
import {
  calculateCurrentBalance as calcCurrentBalance,
  calculateTotalPayouts
} from "../../utils/accountUtils";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import HistoryIcon from "@mui/icons-material/History";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import BacktestAccountCard from "../../components/BacktestAccountCard";
import { GMT_OFFSETS } from "../../utils/timezones";

const AccountManagement = ({ onClose }) => {
  const auth = useContext(AuthContext);
  const theme = useContext(ThemeContext);
  const [accounts, setAccounts] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("create"); // create or edit
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [formData, setFormData] = useState({
    type: "Personal",
    name: "",
    startingBalance: "",
    currency: "USD",
    profitSplit: "",
    serverTimeWinterOffset: "+00:00",
    serverTimeSummerOffset: "+00:00",
    serverTimeDstStart: "",
    serverTimeDstEnd: "",
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactionType, setTransactionType] = useState("deposit"); // deposit, withdrawal, or payout
  const [transactionAmount, setTransactionAmount] = useState("");
  const [transactionNote, setTransactionNote] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [viewTransactionsOpen, setViewTransactionsOpen] = useState(false);
  const [transactionsAccount, setTransactionsAccount] = useState(null);
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [payoutAccount, setPayoutAccount] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const fetchedAccounts = await accountService.getAccounts();
      setAccounts(fetchedAccounts);
    } catch (err) {
      console.error("Error loading accounts:", err);
      throwMessage("error", "Failed to load accounts");
    }
  };

  const handleOpenCreate = () => {
    setDialogMode("create");
    setFormData({
      type: "Personal",
      name: "",
      startingBalance: "",
      currency: "USD",
      profitSplit: "",
      serverTimeWinterOffset: "+00:00",
      serverTimeSummerOffset: "+00:00",
      serverTimeSwitchDate: "",
    });
    setSelectedAccount(null);
    setOpenDialog(true);
  };

  const handleOpenEdit = (account) => {
    setDialogMode("edit");
    setFormData({
      type: account.type,
      name: account.name,
      startingBalance: account.startingBalance,
      currency: account.currency || "USD",
      profitSplit: account.profitSplit || "",
      serverTimeWinterOffset: account.serverTimeWinterOffset || "+00:00",
      serverTimeSummerOffset: account.serverTimeSummerOffset || "+00:00",
      serverTimeDstStart: account.serverTimeDstStart || "",
      serverTimeDstEnd: account.serverTimeDstEnd || "",
    });
    setSelectedAccount(account);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedAccount(null);
    setFormData({
      type: "Personal",
      name: "",
      startingBalance: "",
      currency: "USD",
      profitSplit: "",
      serverTimeWinterOffset: "+00:00",
      serverTimeSummerOffset: "+00:00",
      serverTimeSwitchDate: "",
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.startingBalance) {
      throwMessage("warning", "Fill in all fields");
      return;
    }

    try {
      if (dialogMode === "create") {
        const activeAccountsOfType = accounts.filter(
          (acc) => acc.type === formData.type && acc.isActive
        );
        if (activeAccountsOfType.length >= 5) {
          throwMessage("warning", `Maximum of 5 ${formData.type} accounts reached`);
          return;
        }

        await accountService.createAccount({
          type: formData.type,
          name: formData.name,
          startingBalance: formData.startingBalance,
          currency: formData.currency,
          profitSplit: formData.profitSplit,
          serverTimeWinterOffset: formData.serverTimeWinterOffset,
          serverTimeSummerOffset: formData.serverTimeSummerOffset,
          serverTimeDstStart: formData.serverTimeDstStart,
          serverTimeDstEnd: formData.serverTimeDstEnd,
        });

        await loadAccounts();
        window.dispatchEvent(new Event("accounts-updated"));
        handleCloseDialog();
      } else {
        await accountService.updateAccount(selectedAccount.id, {
          name: formData.name,
          startingBalance: formData.startingBalance,
          currency: formData.currency,
          profitSplit: formData.profitSplit,
          serverTimeWinterOffset: formData.serverTimeWinterOffset,
          serverTimeSummerOffset: formData.serverTimeSummerOffset,
          serverTimeDstStart: formData.serverTimeDstStart,
          serverTimeDstEnd: formData.serverTimeDstEnd,
        });

        await loadAccounts();
        window.dispatchEvent(new Event("accounts-updated"));
        handleCloseDialog();
      }
    } catch (err) {
      console.error("Error saving account:", err);
    }
  };

  const handleArchiveToggle = async (account) => {
    try {
      await accountService.updateAccount(account.id, {
        isActive: !account.isActive,
      });

      await loadAccounts();
      window.dispatchEvent(new Event("accounts-updated"));
      throwMessage(
        "success",
        account.isActive ? "Account archived" : "Account restored"
      );
    } catch (err) {
      console.error("Error toggling archive:", err);
    }
  };

  const handleDeleteClick = (account) => {
    setAccountToDelete(account);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await accountService.deleteAccount(accountToDelete.id);

      await loadAccounts();
      window.dispatchEvent(new Event("accounts-updated"));
      setDeleteConfirmOpen(false);
      setAccountToDelete(null);
    } catch (err) {
      console.error("Error deleting account:", err);
    }
  };

  const handleOpenTransaction = (account, type) => {
    setSelectedAccount(account);
    setTransactionType(type);
    setTransactionAmount("");
    setTransactionNote("");
    setTransactionDate(dayjs().format('YYYY-MM-DD'));
    setTransactionDialogOpen(true);
  };

  const handleCloseTransaction = () => {
    setTransactionDialogOpen(false);
    setSelectedAccount(null);
    setTransactionAmount("");
    setTransactionNote("");
    setTransactionDate("");
  };

  const handleSubmitTransaction = async () => {
    if (!transactionAmount || parseFloat(transactionAmount) <= 0) {
      throwMessage("warning", "Enter a valid amount");
      return;
    }

    if (!transactionDate) {
      throwMessage("warning", "Please select a transaction date");
      return;
    }

    try {
      const dateToSend = transactionDate ? new Date(transactionDate).toISOString() : new Date().toISOString();
      
      await accountService.addTransaction(selectedAccount.id, {
        type: transactionType,
        amount: parseFloat(transactionAmount),
        note: transactionNote,
        date: dateToSend,
      });

      await loadAccounts();
      window.dispatchEvent(new Event("accounts-updated"));
      handleCloseTransaction();
    } catch (err) {
      console.error("Error adding transaction:", err);
    }
  };

  const handleViewTransactions = (account) => {
    setTransactionsAccount(account);
    setViewTransactionsOpen(true);
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!transactionsAccount) return;

    try {
      await accountService.deleteTransaction(transactionsAccount.id, transactionId);
      await loadAccounts();
      window.dispatchEvent(new Event("accounts-updated"));
      
      const updatedAccounts = await accountService.getAccounts();
      const updatedAccount = updatedAccounts.find(acc => acc.id === transactionsAccount.id);
      setTransactionsAccount(updatedAccount);
    } catch (err) {
      console.error("Error deleting transaction:", err);
    }
  };

  const calculateCurrentBalance = (account) => {
    return calcCurrentBalance(account, auth.trades || []);
  };

  const handleOpenPayout = (account) => {
    const currentBalance = calculateCurrentBalance(account);
    const originalBalance = parseFloat(account.originalBalance || account.startingBalance);
    const maxPayout = Math.max(0, currentBalance - originalBalance);
    
    setPayoutAccount(account);
    setPayoutAmount(maxPayout.toFixed(2));
    setPayoutDialogOpen(true);
  };

  const handleClosePayout = () => {
    setPayoutDialogOpen(false);
    setPayoutAccount(null);
    setPayoutAmount("");
  };

  const handleSubmitPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      throwMessage("warning", "Enter a valid payout amount");
      return;
    }

    const currentBalance = calculateCurrentBalance(payoutAccount);
    const originalBalance = parseFloat(payoutAccount.originalBalance || payoutAccount.startingBalance);
    const maxPayout = currentBalance - originalBalance;

    if (parseFloat(payoutAmount) > maxPayout) {
      throwMessage("warning", `Maximum payout is ${maxPayout.toFixed(2)}`);
      return;
    }

    try {
      const result = await accountService.addTransaction(payoutAccount.id, {
        type: "payout",
        amount: parseFloat(payoutAmount),
        note: "Payout",
        date: new Date().toISOString(),
      });

      // Force reload user data from server to update localStorage
      if (result.user) {
        localStorage.setItem("user", JSON.stringify(result.user));
      }

      await loadAccounts();
      window.dispatchEvent(new Event("accounts-updated"));
      handleClosePayout();
      throwMessage("success", "Payout recorded successfully");
    } catch (err) {
      console.error("Error recording payout:", err);
    }
  };

  const activeAccounts = accounts.filter((acc) => acc.isActive);
  const archivedAccounts = accounts.filter((acc) => !acc.isActive);

  const getAccountIcon = (type) => {
    return type === "Personal" ? (
      <SavingsOutlinedIcon />
    ) : (
      <AccountBalanceOutlinedIcon />
    );
  };

  return (
    <Box sx={{ 
      p: { xs: 2, sm: 3, md: 4 },
      backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
      minHeight: "100%",
      color: theme[0] === "dark" ? "#FCFCFF" : "black",
      maxWidth: "1400px",
      margin: "0 auto"
    }}>
      {!auth.backtestingMode && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            disabled={activeAccounts.length >= 10}
            sx={{
              backgroundColor: theme[0] === "dark" ? "rgba(38, 166, 93, 0.15)" : "rgba(38, 166, 93, 0.1)",
              color: "#26a65d",
              border: theme[0] === "dark" ? "1px solid rgba(38, 166, 93, 0.4)" : "1px solid rgba(38, 166, 93, 0.3)",
              textTransform: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              fontWeight: "600",
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(38, 166, 93, 0.25)" : "rgba(38, 166, 93, 0.15)",
                borderColor: theme[0] === "dark" ? "rgba(38, 166, 93, 0.5)" : "rgba(38, 166, 93, 0.4)",
                boxShadow: "none"
              },
              "&:disabled": {
                backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                color: theme[0] === "dark" ? "#6b7280" : "#9ca3af",
                border: theme[0] === "dark" ? "1px solid #3a3b3e" : "1px solid #d1d5db"
              }
            }}
          >
            Create Account
          </Button>
          {onClose && (
            <Button
              variant="outlined"
              startIcon={<CloseIcon />}
              onClick={onClose}
              sx={{
                color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                borderColor: theme[0] === "dark" ? "#2d2e33" : "#e5e7eb",
                textTransform: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: "600",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: theme[0] === "dark" ? "#3a3b3e" : "#d1d5db",
                  backgroundColor: theme[0] === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)"
                }
              }}
            >
              Close
            </Button>
          )}
        </Box>
      )}

      {/* Active Accounts */}
      {!auth.backtestingMode && (
        <>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              fontWeight: "600",
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}
          >
            Active Accounts ({activeAccounts.length}/10)
          </Typography>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 4 }}>
            <TableContainer 
              component={Paper}
              sx={{
                bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
                borderRadius: "12px",
                border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                boxShadow: theme[0] === "dark" 
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(0, 0, 0, 0.08)",
                overflow: "hidden"
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                      "& th": {
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        fontWeight: "700",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                        py: 2
                      }
                    }}
                  >
                    <TableCell>Account</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Starting Balance</TableCell>
                    <TableCell align="right">Current Balance</TableCell>
                    <TableCell align="right">Cycle Earnings</TableCell>
                    <TableCell align="center">Profit Split</TableCell>
                    <TableCell align="center">Transactions</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeAccounts.map((account) => {
                    const currentBalance = calculateCurrentBalance(account);
                    const startBalance = account.type === "Investor" && account.originalBalance 
                      ? parseFloat(account.originalBalance) 
                      : parseFloat(account.startingBalance);
                    const cycleEarnings = currentBalance - startBalance;
                    const lifetimeEarnings = account.type === "Investor" && account.originalBalance
                      ? cycleEarnings + calculateTotalPayouts(account)
                      : null;

                    return (
                      <TableRow
                        key={account.id}
                        sx={{
                          "&:hover": {
                            bgcolor: theme[0] === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"
                          },
                          "& td": {
                            color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                            borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                            py: 2.5
                          }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {getAccountIcon(account.type)}
                            <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                              {account.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={account.type}
                            size="small"
                            sx={{ 
                              backgroundColor: account.type === "Personal" ? "#2196f3" : "#9c27b0",
                              color: "#fff",
                              fontSize: "11px",
                              height: "24px",
                              fontWeight: "600"
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                            {account.currency || "USD"} ${startBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            sx={{ 
                              fontSize: "14px", 
                              fontWeight: "700",
                              color: currentBalance < startBalance
                                ? "#f23645"
                                : theme[0] === "dark" ? "#4CAF50" : "#2e7d32"
                            }}
                          >
                            {account.currency || "USD"} ${currentBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography 
                              sx={{ 
                                fontSize: "14px", 
                                fontWeight: "600",
                                color: cycleEarnings < 0
                                  ? "#f23645"
                                  : cycleEarnings > 0
                                  ? theme[0] === "dark" ? "#4CAF50" : "#2e7d32"
                                  : theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)"
                              }}
                            >
                              {cycleEarnings >= 0 ? "+" : ""}{account.currency || "USD"} ${cycleEarnings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </Typography>
                            {lifetimeEarnings !== null && (
                              <Typography 
                                sx={{ 
                                  fontSize: "11px", 
                                  color: theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                                  mt: 0.5
                                }}
                              >
                                Lifetime: {lifetimeEarnings >= 0 ? "+" : ""}{account.currency || "USD"} ${lifetimeEarnings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                            {account.profitSplit ? `${account.profitSplit}%` : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography sx={{ fontSize: "14px" }}>
                            {(account.transactions || []).length}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                            {account.type === "Investor" && account.originalBalance && (
                              <Tooltip title="Payout">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenPayout(account)}
                                    disabled={calculateCurrentBalance(account) <= parseFloat(account.originalBalance)}
                                    sx={{
                                      color: "#9c27b0",
                                      "&:disabled": {
                                        color: "rgba(0,0,0,0.26)"
                                      }
                                    }}
                                  >
                                    <SavingsOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                            <Tooltip title="Deposit">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenTransaction(account, "deposit")}
                                sx={{ color: "#4CAF50" }}
                              >
                                <AddCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {account.type !== "Investor" && (
                              <Tooltip title="Withdrawal">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleOpenTransaction(account, "withdrawal")}
                                  sx={{ color: "#f44336" }}
                                >
                                  <RemoveCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="History">
                              <IconButton 
                                size="small" 
                                onClick={() => handleViewTransactions(account)}
                                sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                              >
                                <HistoryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton 
                                size="small" 
                                onClick={() => handleOpenEdit(account)}
                                sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Archive">
                              <IconButton 
                                size="small" 
                                onClick={() => handleArchiveToggle(account)}
                                sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                              >
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteClick(account)}
                                sx={{ color: "#f23645" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Grid container spacing={2}>
              {activeAccounts.map((account) => {
                const currentBalance = calculateCurrentBalance(account);
                const startBalance = account.type === "Investor" && account.originalBalance 
                  ? parseFloat(account.originalBalance) 
                  : parseFloat(account.startingBalance);
                const cycleEarnings = currentBalance - startBalance;
                const lifetimeEarnings = account.type === "Investor" && account.originalBalance
                  ? cycleEarnings + calculateTotalPayouts(account)
                  : null;

                return (
                  <Grid item xs={12} key={account.id}>
                    <Card
                      sx={{
                        bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
                        color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        borderRadius: "12px",
                        border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                        boxShadow: theme[0] === "dark" 
                          ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                          : "0 4px 12px rgba(0, 0, 0, 0.08)"
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {getAccountIcon(account.type)}
                            <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "600" }}>
                              {account.name}
                            </Typography>
                          </Box>
                          <Chip
                            label={account.type}
                            size="small"
                            sx={{ 
                              backgroundColor: account.type === "Personal" ? "#2196f3" : "#9c27b0",
                              color: "#fff",
                              fontSize: "11px",
                              height: "24px",
                              fontWeight: "600"
                            }}
                          />
                        </Box>

                        <Box sx={{ 
                          display: "grid", 
                          gridTemplateColumns: "1fr 1fr", 
                          gap: 2, 
                          mb: 2,
                          pb: 2,
                          borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb"
                        }}>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 0.5
                              }}
                            >
                              Starting
                            </Typography>
                            <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                              {account.currency || "USD"} ${startBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 0.5
                              }}
                            >
                              Current
                            </Typography>
                            <Typography 
                              sx={{ 
                                fontSize: "14px", 
                                fontWeight: "700",
                                color: currentBalance < startBalance
                                  ? "#f23645"
                                  : theme[0] === "dark" ? "#4CAF50" : "#2e7d32"
                              }}
                            >
                              {account.currency || "USD"} ${currentBalance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 0.5
                              }}
                            >
                              Cycle Earnings
                            </Typography>
                            <Typography 
                              sx={{ 
                                fontSize: "14px", 
                                fontWeight: "600",
                                color: cycleEarnings < 0
                                  ? "#f23645"
                                  : cycleEarnings > 0
                                  ? theme[0] === "dark" ? "#4CAF50" : "#2e7d32"
                                  : theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)"
                              }}
                            >
                              {cycleEarnings >= 0 ? "+" : ""}{account.currency || "USD"} ${cycleEarnings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 0.5
                              }}
                            >
                              Transactions
                            </Typography>
                            <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                              {(account.transactions || []).length}
                            </Typography>
                          </Box>
                        </Box>

                        {lifetimeEarnings !== null && (
                          <Box sx={{ mb: 2 }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)",
                                fontSize: "11px",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                display: "block",
                                mb: 0.5
                              }}
                            >
                              Lifetime Earnings
                            </Typography>
                            <Typography 
                              sx={{ 
                                fontSize: "14px", 
                                fontWeight: "600",
                                color: lifetimeEarnings < 0
                                  ? "#f23645"
                                  : lifetimeEarnings > 0
                                  ? theme[0] === "dark" ? "#4CAF50" : "#2e7d32"
                                  : theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)"
                              }}
                            >
                              {lifetimeEarnings >= 0 ? "+" : ""}{account.currency || "USD"} ${lifetimeEarnings.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
                          {account.type === "Investor" && account.originalBalance && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenPayout(account)}
                              disabled={calculateCurrentBalance(account) <= parseFloat(account.originalBalance)}
                              sx={{
                                color: "#9c27b0",
                                borderColor: "#9c27b0",
                                textTransform: "none",
                                fontSize: "11px",
                                minWidth: "auto",
                                px: 1.5,
                                py: 0.5,
                                "&:hover": {
                                  borderColor: "#7b1fa2",
                                  backgroundColor: "rgba(156, 39, 176, 0.08)"
                                },
                                "&:disabled": {
                                  borderColor: "rgba(0,0,0,0.12)",
                                  color: "rgba(0,0,0,0.26)"
                                }
                              }}
                            >
                              Payout
                            </Button>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenTransaction(account, "deposit")}
                            sx={{ color: "#4CAF50" }}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                          {account.type !== "Investor" && (
                            <IconButton 
                              size="small" 
                              onClick={() => handleOpenTransaction(account, "withdrawal")}
                              sx={{ color: "#f44336" }}
                            >
                              <RemoveCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewTransactions(account)}
                            sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenEdit(account)}
                            sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleArchiveToggle(account)}
                            sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(account)}
                            sx={{ color: "#f23645" }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </>
      )}

     
      {auth.backtestingMode && (
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} lg={4} sx={{ minWidth: "300px" }}>
            <BacktestAccountCard />
          </Grid>
        </Grid>
      )}

      {/* Archived Accounts */}
      {!auth.backtestingMode && archivedAccounts.length > 0 && (
        <>
          <Typography 
            variant="h6" 
            sx={{ 
              mb: 3, 
              mt: 5,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              fontWeight: "600",
              fontSize: "13px",
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}
          >
            Archived Accounts ({archivedAccounts.length})
          </Typography>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 4 }}>
            <TableContainer 
              component={Paper}
              sx={{
                bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
                borderRadius: "12px",
                border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                boxShadow: theme[0] === "dark" 
                  ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                  : "0 4px 12px rgba(0, 0, 0, 0.08)",
                overflow: "hidden"
              }}
            >
              <Table>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
                      "& th": {
                        color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                        fontWeight: "700",
                        fontSize: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                        py: 2
                      }
                    }}
                  >
                    <TableCell>Account</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Starting Balance</TableCell>
                    <TableCell align="center">Profit Split</TableCell>
                    <TableCell align="center">Transactions</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archivedAccounts.map((account) => (
                    <TableRow
                      key={account.id}
                      sx={{
                        "&:hover": {
                          bgcolor: theme[0] === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"
                        },
                        "& td": {
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                          borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                          py: 2.5
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getAccountIcon(account.type)}
                          <Box>
                            <Typography sx={{ fontWeight: "600", fontSize: "14px" }}>
                              {account.name}
                            </Typography>
                            <Chip 
                              label="Archived" 
                              size="small" 
                              sx={{ 
                                mt: 0.5,
                                height: "20px",
                                fontSize: "10px",
                                backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.15)",
                                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                              }} 
                            />
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.type}
                          size="small"
                          sx={{ 
                            backgroundColor: account.type === "Personal" ? "rgba(33, 150, 243, 0.5)" : "rgba(156, 39, 176, 0.5)",
                            color: "#fff",
                            fontSize: "11px",
                            height: "24px",
                            fontWeight: "600"
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: "14px", fontWeight: "500" }}>
                          {account.currency || "USD"} ${parseFloat(account.startingBalance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                          {account.profitSplit ? `${account.profitSplit}%` : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: "14px" }}>
                          {(account.transactions || []).length}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                          <Tooltip title="Restore">
                            <IconButton 
                              size="small" 
                              onClick={() => handleArchiveToggle(account)}
                              sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                            >
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              onClick={() => handleDeleteClick(account)}
                              sx={{ color: "#f23645" }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <Grid container spacing={2}>
              {archivedAccounts.map((account) => (
                <Grid item xs={12} key={account.id}>
                  <Card
                    sx={{
                      bgcolor: theme[0] === "dark" ? "#25262b" : "#ffffff",
                      color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      borderRadius: "12px",
                      border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
                      boxShadow: theme[0] === "dark" 
                        ? "0 4px 12px rgba(0, 0, 0, 0.3)"
                        : "0 4px 12px rgba(0, 0, 0, 0.08)"
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {getAccountIcon(account.type)}
                          <Typography variant="h6" sx={{ fontSize: "16px", fontWeight: "600" }}>
                            {account.name}
                          </Typography>
                        </Box>
                        <Chip
                          label={account.type}
                          size="small"
                          sx={{ 
                            backgroundColor: account.type === "Personal" ? "rgba(33, 150, 243, 0.5)" : "rgba(156, 39, 176, 0.5)",
                            color: "#fff",
                            fontSize: "11px",
                            height: "24px",
                            fontWeight: "600"
                          }}
                        />
                      </Box>

                      <Chip 
                        label="Archived" 
                        size="small" 
                        sx={{ 
                          mb: 2,
                          height: "24px",
                          fontSize: "11px",
                          backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.15)",
                          color: theme[0] === "dark" ? "#9ca3af" : "#6b7280"
                        }} 
                      />

                      <Box sx={{ 
                        display: "grid", 
                        gridTemplateColumns: "1fr 1fr", 
                        gap: 2, 
                        mb: 2,
                        pb: 2,
                        borderBottom: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb"
                      }}>
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                              fontSize: "11px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              display: "block",
                              mb: 0.5
                            }}
                          >
                            Starting Balance
                          </Typography>
                          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                            {account.currency || "USD"} ${parseFloat(account.startingBalance).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)",
                              fontSize: "11px",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              display: "block",
                              mb: 0.5
                            }}
                          >
                            Transactions
                          </Typography>
                          <Typography sx={{ fontSize: "14px", fontWeight: "600" }}>
                            {(account.transactions || []).length}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleArchiveToggle(account)}
                          sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
                        >
                          <UnarchiveIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteClick(account)}
                          sx={{ color: "#f23645" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#ffffff",
            color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
            borderRadius: "16px",
            border: theme[0] === "dark" ? "1px solid #2d2e33" : "1px solid #e5e7eb",
            boxShadow: theme[0] === "dark" 
              ? "0 20px 60px rgba(0, 0, 0, 0.8)"
              : "0 20px 60px rgba(0, 0, 0, 0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          fontWeight: "600",
          fontSize: "20px",
          letterSpacing: "-0.5px",
          pb: 1
        }}>
          {dialogMode === "create" ? "Create New Account" : "Edit Account"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel 
                sx={{ 
                  color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
                  "&.Mui-focused": {
                    color: theme[0] === "dark" ? "#FCFCFF" : "#1976d2",
                  },
                  "&.Mui-disabled": {
                    color: theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0, 0, 0, 0.38)",
                  },
                }}
              >
                Account Type
              </InputLabel>
              <Select
                value={formData.type}
                label="Account Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                disabled={dialogMode === "edit"}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#fff",
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                      "& .MuiMenuItem-root": {
                        color: theme[0] === "dark" ? "#FCFCFF" : "black",
                        "&:hover": {
                          backgroundColor: theme[0] === "dark" ? "#25262b" : "#f5f5f5",
                        },
                        "&.Mui-selected": {
                          backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e3f2fd",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: theme[0] === "dark" ? "#3a3b3e" : "#bbdefb",
                        },
                      },
                    },
                  },
                }}
                sx={{
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& .MuiSelect-select": {
                    color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  },
                  "&.Mui-disabled .MuiSelect-select": {
                    color: theme[0] === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0, 0, 0, 0.38)",
                    WebkitTextFillColor: theme[0] === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0, 0, 0, 0.38)",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "#1976d2",
                  },
                  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0, 0, 0, 0.26)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.54)",
                  },
                  "&.Mui-disabled .MuiSvgIcon-root": {
                    color: theme[0] === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0, 0, 0, 0.26)",
                  },
                }}
              >
                <MenuItem value="Personal">Personal</MenuItem>
                <MenuItem value="Investor">Investor</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Account Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ 
                mb: 2,
                "& .MuiInputLabel-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiOutlinedInput-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& fieldset": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Starting Balance"
              type="number"
              value={formData.startingBalance}
              onChange={(e) =>
                setFormData({ ...formData, startingBalance: e.target.value })
              }
              sx={{ 
                mb: 2,
                "& .MuiInputLabel-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiOutlinedInput-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& fieldset": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                },
              }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)" }}>
                Currency
              </InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#fff",
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                      "& .MuiMenuItem-root": {
                        "&:hover": {
                          backgroundColor: theme[0] === "dark" ? "#25262b" : "#f5f5f5",
                        },
                        "&.Mui-selected": {
                          backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e3f2fd",
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: theme[0] === "dark" ? "#3a3b3e" : "#bbdefb",
                        },
                      },
                    },
                  },
                }}
                sx={{
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                  "& .MuiSvgIcon-root": {
                    color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.54)",
                  },
                }}
              >
                <MenuItem value="USD">USD ($)</MenuItem>
                <MenuItem value="GBP">GBP (£)</MenuItem>
                <MenuItem value="AUD">AUD (A$)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Profit Split %"
              type="number"
              value={formData.profitSplit}
              onChange={(e) =>
                setFormData({ ...formData, profitSplit: e.target.value })
              }

              sx={{
                "& .MuiInputLabel-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiOutlinedInput-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& fieldset": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                },
              }}
            />
            <Typography
              sx={{
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "0.75rem",
                fontWeight: "600",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1,
                mt: 1,
              }}
            >
              Server Time
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)" }}>
                    Winter Offset
                  </InputLabel>
                  <Select
                    value={formData.serverTimeWinterOffset || "+00:00"}
                    label="Winter Offset"
                    onChange={(e) =>
                      setFormData({ ...formData, serverTimeWinterOffset: e.target.value })
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#fff",
                          color: theme[0] === "dark" ? "#FCFCFF" : "black",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: theme[0] === "dark" ? "#25262b" : "#f5f5f5",
                            },
                            "&.Mui-selected": {
                              backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e3f2fd",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: theme[0] === "dark" ? "#3a3b3e" : "#bbdefb",
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                      },
                      "& .MuiSvgIcon-root": {
                        color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.54)",
                      },
                    }}
                  >
                    {GMT_OFFSETS.map((offset) => (
                      <MenuItem key={offset.value} value={offset.value}>
                        {offset.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)" }}>
                    Summer Offset
                  </InputLabel>
                  <Select
                    value={formData.serverTimeSummerOffset || "+00:00"}
                    label="Summer Offset"
                    onChange={(e) =>
                      setFormData({ ...formData, serverTimeSummerOffset: e.target.value })
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#fff",
                          color: theme[0] === "dark" ? "#FCFCFF" : "black",
                          "& .MuiMenuItem-root": {
                            "&:hover": {
                              backgroundColor: theme[0] === "dark" ? "#25262b" : "#f5f5f5",
                            },
                            "&.Mui-selected": {
                              backgroundColor: theme[0] === "dark" ? "#2d2e33" : "#e3f2fd",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: theme[0] === "dark" ? "#3a3b3e" : "#bbdefb",
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      color: theme[0] === "dark" ? "#FCFCFF" : "black",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                      },
                      "& .MuiSvgIcon-root": {
                        color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.54)",
                      },
                    }}
                  >
                    {GMT_OFFSETS.map((offset) => (
                      <MenuItem key={offset.value} value={offset.value}>
                        {offset.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <CustomDatePicker
                  label="DST Start"
                  format="MM-DD"
                  value={formData.serverTimeDstStart ? `2024-${formData.serverTimeDstStart}` : null}
                  onChange={(date) =>
                    setFormData({
                      ...formData,
                      serverTimeDstStart: date ? dayjs(date).format("MM-DD") : "",
                    })
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <CustomDatePicker
                  label="DST End"
                  format="MM-DD"
                  value={formData.serverTimeDstEnd ? `2024-${formData.serverTimeDstEnd}` : null}
                  onChange={(date) =>
                    setFormData({
                      ...formData,
                      serverTimeDstEnd: date ? dayjs(date).format("MM-DD") : "",
                    })
                  }
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.12)" : "rgba(107, 114, 128, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.16)"}`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.16)" : "rgba(107, 114, 128, 0.12)",
                transform: "scale(1.05)"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(38, 166, 93, 0.12)" : "rgba(38, 166, 93, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(38, 166, 93, 0.2)" : "rgba(38, 166, 93, 0.16)"}`,
              color: "#26a65d",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(38, 166, 93, 0.16)" : "rgba(38, 166, 93, 0.12)",
                transform: "scale(1.05)",
                boxShadow: "none"
              }
            }}
          >
            {dialogMode === "create" ? "Create" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmOpen} 
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            backgroundColor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
            borderRadius: "12px"
          }
        }}
      >
        <DialogTitle sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}>
            Are you sure you want to delete "{accountToDelete?.name}"? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button 
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.12)" : "rgba(107, 114, 128, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.16)"}`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.16)" : "rgba(107, 114, 128, 0.12)",
                transform: "scale(1.05)"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(242, 54, 69, 0.12)" : "rgba(242, 54, 69, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(242, 54, 69, 0.2)" : "rgba(242, 54, 69, 0.16)"}`,
              color: "#f23645",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(242, 54, 69, 0.16)" : "rgba(242, 54, 69, 0.12)",
                transform: "scale(1.05)",
                boxShadow: "none"
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Dialog (Deposit/Withdrawal) */}
      <Dialog 
        open={transactionDialogOpen} 
        onClose={handleCloseTransaction}
        PaperProps={{
          sx: {
            backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#FCFCFF",
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
            borderRadius: "12px",
            minWidth: "400px"
          }
        }}
      >
        <DialogTitle sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}>
          {transactionType === "deposit" ? "Deposit Funds" : "Withdraw Funds"} - {selectedAccount?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={transactionAmount}
              onChange={(e) => setTransactionAmount(e.target.value)}
              sx={{ 
                mb: 2,
                "& .MuiInputLabel-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiOutlinedInput-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& fieldset": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                },
              }}
            />
            <Box sx={{ mb: 2 }}>
              <CustomDatePicker
                label="Transaction Date"
                value={transactionDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setTransactionDate(dayjs(newValue).format('YYYY-MM-DD'));
                  }
                }}
              />
            </Box>
            <TextField
              fullWidth
              label="Note (optional)"
              value={transactionNote}
              onChange={(e) => setTransactionNote(e.target.value)}
              multiline
              rows={3}
              sx={{ 
                "& .MuiInputLabel-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
                },
                "& .MuiOutlinedInput-root": {
                  color: theme[0] === "dark" ? "#FCFCFF" : "black",
                  "& fieldset": {
                    borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button 
            onClick={handleCloseTransaction}
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.12)" : "rgba(107, 114, 128, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.16)"}`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.16)" : "rgba(107, 114, 128, 0.12)",
                transform: "scale(1.05)"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitTransaction} 
            variant="contained"
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: transactionType === "deposit" 
                ? theme[0] === "dark" ? "rgba(76, 175, 80, 0.12)" : "rgba(76, 175, 80, 0.08)"
                : theme[0] === "dark" ? "rgba(244, 67, 54, 0.12)" : "rgba(244, 67, 54, 0.08)",
              border: `1px solid ${transactionType === "deposit" 
                ? theme[0] === "dark" ? "rgba(76, 175, 80, 0.2)" : "rgba(76, 175, 80, 0.16)"
                : theme[0] === "dark" ? "rgba(244, 67, 54, 0.2)" : "rgba(244, 67, 54, 0.16)"}`,
              color: transactionType === "deposit" ? "#4CAF50" : "#f44336",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: transactionType === "deposit" 
                  ? theme[0] === "dark" ? "rgba(76, 175, 80, 0.16)" : "rgba(76, 175, 80, 0.12)"
                  : theme[0] === "dark" ? "rgba(244, 67, 54, 0.16)" : "rgba(244, 67, 54, 0.12)",
                transform: "scale(1.05)",
                boxShadow: "none"
              }
            }}
          >
            {transactionType === "deposit" ? "Deposit" : "Withdraw"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog 
        open={viewTransactionsOpen} 
        onClose={() => setViewTransactionsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#FCFCFF",
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
            borderRadius: "12px"
          }
        }}
      >
        <DialogTitle sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}>
          Transaction History - {transactionsAccount?.name}
        </DialogTitle>
        <DialogContent>
          {transactionsAccount && (transactionsAccount.transactions || []).length > 0 ? (
            <List>
              {[...(transactionsAccount.transactions || [])].reverse().map((txn, index) => (
                <React.Fragment key={txn.id || index}>
                  <ListItem
                    secondaryAction={
                      <IconButton 
                        edge="end" 
                        aria-label="delete"
                        onClick={() => handleDeleteTransaction(txn.id)}
                        sx={{
                          color: "#f44336",
                          "&:hover": {
                            backgroundColor: theme[0] === "dark" ? "rgba(244, 67, 54, 0.1)" : "rgba(244, 67, 54, 0.08)"
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pr: 2 }}>
                          <Typography sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black", fontWeight: "500" }}>
                            {txn.type === "deposit" ? "➕ Deposit" : txn.type === "payout" ? "💰 Payout" : "➖ Withdrawal"}
                          </Typography>
                          <Typography 
                            sx={{ 
                              color: txn.type === "deposit" ? "#4CAF50" : txn.type === "payout" ? "#9c27b0" : "#f44336",
                              fontWeight: "600"
                            }}
                          >
                            {txn.type === "deposit" ? "+" : "-"}{transactionsAccount.currency || "USD"} ${parseFloat(txn.amount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" sx={{ color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)" }}>
                            {new Date(txn.date).toLocaleString()}
                          </Typography>
                          {txn.note && (
                            <Typography variant="body2" sx={{ color: theme[0] === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)", mt: 0.5 }}>
                              {txn.note}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < (transactionsAccount.transactions || []).length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Typography sx={{ color: theme[0] === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.6)", textAlign: "center", py: 4 }}>
              No transactions yet
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button 
            onClick={() => setViewTransactionsOpen(false)}
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.12)" : "rgba(107, 114, 128, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.16)"}`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.16)" : "rgba(107, 114, 128, 0.12)",
                transform: "scale(1.05)"
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payout Dialog */}
      <Dialog 
        open={payoutDialogOpen} 
        onClose={handleClosePayout}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#FCFCFF",
            color: theme[0] === "dark" ? "#FCFCFF" : "black",
            borderRadius: "12px",
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600",
          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
          borderBottom: `1px solid ${theme[0] === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          pb: 2
        }}>
          Record Payout - {payoutAccount?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ pt: 2, mb: 1, color: theme[0] === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)" }}>
              Current Balance: {payoutAccount?.currency || "USD"} ${payoutAccount ? calculateCurrentBalance(payoutAccount).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: theme[0] === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)" }}>
              Funded Amount: {payoutAccount?.currency || "USD"} ${payoutAccount ? parseFloat(payoutAccount.originalBalance || payoutAccount.startingBalance).toLocaleString() : "0.00"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#9c27b0", fontWeight: "600" }}>
              Available for Payout: {payoutAccount?.currency || "USD"} ${payoutAccount ? Math.max(0, calculateCurrentBalance(payoutAccount) - parseFloat(payoutAccount.originalBalance || payoutAccount.startingBalance)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : "0.00"}
            </Typography>
          </Box>
          <TextField
            label="Payout Amount"
            type="number"
            fullWidth
            value={payoutAmount}
            onChange={(e) => setPayoutAmount(e.target.value)}
            sx={{ 
              mb: 2,
              "& .MuiInputLabel-root": {
                color: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.6)",
              },
              "& .MuiOutlinedInput-root": {
                color: theme[0] === "dark" ? "#FCFCFF" : "black",
                "& fieldset": {
                  borderColor: theme[0] === "dark" ? "rgba(255,255,255,0.23)" : "rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: theme[0] === "dark" ? "#FCFCFF" : "rgba(0, 0, 0, 0.87)",
                },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: theme[0] === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", display: "block", mb: 2 }}>
            After payout, your account balance will return to the funded amount of {payoutAccount?.currency || "USD"} ${payoutAccount ? parseFloat(payoutAccount.originalBalance || payoutAccount.startingBalance).toLocaleString() : "0.00"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button 
            onClick={handleClosePayout}
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.12)" : "rgba(107, 114, 128, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(156, 163, 175, 0.2)" : "rgba(107, 114, 128, 0.16)"}`,
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(156, 163, 175, 0.16)" : "rgba(107, 114, 128, 0.12)",
                transform: "scale(1.05)"
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitPayout} 
            variant="contained"
            sx={{
              padding: "10px 18px",
              borderRadius: "24px",
              backgroundColor: theme[0] === "dark" ? "rgba(156, 39, 176, 0.12)" : "rgba(156, 39, 176, 0.08)",
              border: `1px solid ${theme[0] === "dark" ? "rgba(156, 39, 176, 0.2)" : "rgba(156, 39, 176, 0.16)"}`,
              color: "#9c27b0",
              textTransform: "none",
              fontWeight: "500",
              fontSize: "14px",
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "rgba(156, 39, 176, 0.16)" : "rgba(156, 39, 176, 0.12)",
                transform: "scale(1.05)",
                boxShadow: "none"
              }
            }}
          >
            Record Payout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountManagement;
